import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Slide, styled } from "@mui/material";
import { QuestionsIF } from "../QuestionsIF";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  LocationQuestionsIndex,
  LocationQuestionsMetaData,
  UNISEX,
} from "./locationQuestions/locationQuestionsIndex";
import { LocationMap } from "./locationQuestions/locationMap";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { FeatureAliasMapper } from "../../../state/featureAliasMapper";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { ContinueButton } from "../../uiControllers/continueButton";
import { qualifierValueToQualifierFragment } from "../../../utils/dataHelpers";
import { DocQuestion } from "../DocQuestion";
import { usePatient } from "../../../state/patientContext";
import { useTranslate } from "../../../hooks/useTranslate";
import { TextAnswer } from "../../answers/textAnswer";
import { RefinementQuestion } from "../refinementQuestion";
import { ExternalBackgroundOverlay } from "../../uiControllers/extrenalBackgroundOverlay";
import Fade from "@mui/material/Fade";
import { LocationMapSelector } from "./locationQuestions/locationMapSelector";
import { usePatientFeatureFlags } from "../../../hooks/usePatientFeatureFlags";
import clsx from "clsx";
import { LocationMapsFeatureCounter } from "./locationMapsFeatureCounter";

//region [[ Styles ]]
const LocationRefinementQuestionView = styled(ExternalBackgroundOverlay)({
  "&.inline": {
    background: "transparent",
    margin: 0,
    position: "relative",
  },
});

const OpenButtonContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
  width: "100%",
});

const ButtonQuestion = styled(TextAnswer)({
  cursor: "pointer",
  marginTop: "20px",
});

const MapPopupContainer = styled("div")({
  background: KahunColorMap.White,
  borderTopRightRadius: "14px",
  borderTopLeftRadius: "14px",
  padding: "30px",
  paddingBottom: 0,
  // Give absolute children a place to relate to
  position: "relative",
  ".inline &": {
    padding: 0,
  },
});

const CloseButton = styled(CloseIconButton)({
  ".inline &": {
    display: "none",
  },
  "&.location-close.close-button": {
    right: "15px",
    left: "auto",
  },
});

// const LocationClosedRefinementQuestion = styled(RefinementQuestion)({
//   "& .question-options-container": {
//     boxSizing: "border-box",
//     marginLeft: "-30px",
//     width: "calc(100% + 60px)",
//     marginBottom: 0,
//   },
//   "& .button-area": {
//     width: "calc(100% + 52px)",
//   },
// });

const TopTextArea = styled("div")({
  display: "flex",
  flexFlow: "column",
  padding: "0 15%",
  paddingBottom: "30px",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  ".inline &": {
    paddingBottom: "7px",
  },
});

const QuestionText = styled("div")({
  color: PatientColorMap.Default,
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "19px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ".inline &": {
    display: "none",
  },
});

const Qualifiers = styled("span")({
  display: "inline",
  margin: "0 4px",
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19px",
  color: PatientColorMap.Primary,
  marginTop: "8px",
});

const Footer = styled("div")({
  // Take over the container margin
  margin: "0 -30px",
  width: "calc(100% + 60px)",
  display: "flex",
  flexFlow: "column",
});

const ButtonsDock = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-evenly",
  alignItems: "center",
  padding: "15px",
  background: PatientColorMap.Background2,
  "& > *": {
    margin: "0 5px",
  },
});

const Continue = styled(ContinueButton)({
  "&.continue-button": {
    color: PatientColorMap.ButtonTextColor,
    background: PatientColorMap.Selected,
    "&.disabled": {
      background: PatientColorMap.Unselected,
      color: PatientColorMap.Default,
    },
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface LocationRefinementQuestionProps extends QuestionsIF {
  className?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]
const OPEN_DIAGRAM_BUTTON_TEXT = "OPEN_DIAGRAM_BUTTON_TEXT";
const LOCATION_MAP_TITLE_TEXT = "LOCATION_MAP_TITLE_TEXT";
//endregion [[ Texts ]]

export const LocationRefinementQuestion = ({
  onAnswer,
  question,
  answer,
  ...props
}: LocationRefinementQuestionProps) => {
  const { translate } = useTranslate();

  const [isOpen, setIsOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);

  const [skippedMaps, setSkippedMaps] = useState<number[]>([]);

  /**
   * Keep an index incase there are couple of matching maps
   */
  const [currentIndex, setCurrentIndex] = useState(0);

  // Make sure to reset once a new question object is sent or once an answer is
  // available
  useEffect(() => {
    setSelectedIds([]);
    setSkippedMaps([]);
    setCurrentIndex(0);
    setIsOpen(false);
  }, [question, answer, setSelectedIds, setIsOpen]);

  const { gender } = usePatient();

  const isAnswered = answer !== null && answer !== undefined;

  const suggestion = question.suggestions[0];

  const abstractSuggestion = suggestion.suggestion;

  const refinements = suggestion.refinements;

  const qualifierValues = refinements.flatMap((refinement) =>
    refinement.qualifiers.map((qualifier) => qualifier.value),
  );

  const inlineLocationQuestion = usePatientFeatureFlags("inlineDiagrams");

  // Add or remove to the list of selected elements
  const handleMapItemSelected = useCallback(
    (clickedId: string) => {
      setSelectedIds((selected) => {
        const currentId = selected.findIndex((i) => i === clickedId);
        if (currentId > -1) {
          return [
            ...selected.slice(0, currentId),
            ...selected.slice(currentId + 1),
          ];
        } else {
          return [...selected, clickedId];
        }
      });
    },
    [setSelectedIds],
  );

  const matchGender = useCallback(
    (locationQuestion: LocationQuestionsMetaData) =>
      !gender ||
      !locationQuestion.gender ||
      locationQuestion.gender === UNISEX ||
      locationQuestion.gender === gender,
    [gender],
  );

  // Find all matching location maps, filter out
  const matchingMaps = useMemo(
    () =>
      LocationQuestionsIndex.filter(
        (index) =>
          matchGender(index) &&
          index.rangeId === suggestion.refinementLocationRange?.id,
      ),
    [matchGender, suggestion],
  );

  // Filter maps with no features to select
  const filteredMaps = useMemo(
    () => matchingMaps.filter((_, i) => !skippedMaps.some((m) => m === i)),
    [matchingMaps, skippedMaps],
  );

  const selectedFeatureNames = useMemo(
    () =>
      qualifierValues
        .filter((qualifier) =>
          selectedIds?.some((selected) => selected === qualifier.id),
        )
        ?.map((qualifier) => qualifier.shortname),
    [qualifierValues, selectedIds],
  );

  /***
   * Builds a qualified feature ready to send to the server using onAnswer()
   * */
  const buildQualifiedFeatures = useCallback(
    (qualifiers, skip = false) => {
      const relation = abstractSuggestion.qualifiers[0]?.relation;
      return {
        relation,
        ...abstractSuggestion,
        qualifiers: qualifiers.map((qv) =>
          qualifierValueToQualifierFragment(qv, relation),
        ),
        skip,
      };
    },
    [abstractSuggestion],
  );

  const handleSkip = useCallback(() => {
    // Skip all qualifiers
    onAnswer([buildQualifiedFeatures(qualifierValues, true)]);
  }, [onAnswer, buildQualifiedFeatures, qualifierValues]);

  const handleDone = useCallback(() => {
    const [skipped, added] = qualifierValues.reduce(
      ([toSkip, toAdd], qv) => {
        if (selectedIds.some((sId) => sId === qv.id)) {
          toAdd = [...toAdd, qv];
        } else {
          toSkip = [...toSkip, qv];
        }

        return [toSkip, toAdd];
      },
      [[], []],
    );

    const answerQfs = [];

    if (skipped?.length) answerQfs.push(buildQualifiedFeatures(skipped, true));

    if (added?.length) answerQfs.push(buildQualifiedFeatures(added));

    onAnswer(answerQfs);
  }, [selectedIds, qualifierValues, buildQualifiedFeatures, onAnswer]);

  const handleCloseClicked = useCallback(() => {
    if (selectedIds.length) handleDone();
    else handleSkip();
  }, [handleSkip, selectedIds, handleDone]);

  const handleMapChanged = useCallback(
    (newSelectedIndex) => setCurrentIndex(newSelectedIndex),
    [setCurrentIndex],
  );

  const supportedFeatures = useMemo(() => qualifierValues?.map((qf) => qf.id), [
    qualifierValues,
  ]);

  const handleFeatureCountReceivedForMap = (index, count) => {
    if (!count) {
      // Update by making sure we dont have this index in skipped maps
      setSkippedMaps((olderMaps) =>
        !olderMaps.some((m) => m === index) ? [...olderMaps, index] : olderMaps,
      );
    }
  };
  if (isAnswered) {
    return (
      <DocQuestion
        text={FeatureAliasMapper.mapText(suggestion?.question?.text)}
        miniView={props.miniView}
      />
    );
  }

  /* If we don't have a supporting location map for the presented
            features just render a regular closed question */
  if (!filteredMaps?.length)
    return (
      <RefinementQuestion
        onAnswer={onAnswer}
        question={question}
        answer={answer}
        {...props}
      />
    );

  return (
    <>
      <DocQuestion
        text={FeatureAliasMapper.mapText(suggestion?.question?.text)}
        miniView={props.miniView}
      />
      {!inlineLocationQuestion && (
        <OpenButtonContainer>
          <ButtonQuestion
            answerText={translate(OPEN_DIAGRAM_BUTTON_TEXT)}
            onClick={() => setIsOpen(true)}
            gender={gender}
          />
        </OpenButtonContainer>
      )}
      {/* This is just to calculate the number of features using the DOM */}
      <LocationMapsFeatureCounter
        maps={matchingMaps}
        onSupportedElementCount={handleFeatureCountReceivedForMap}
        supportedFeatures={supportedFeatures}
      />
      <Fade
        in={!!inlineLocationQuestion || isOpen}
        timeout={50}
        mountOnEnter
        unmountOnExit
      >
        <LocationRefinementQuestionView
          className={clsx(
            props.className,
            inlineLocationQuestion ? "inline" : "overlay",
          )}
        >
          <Slide
            direction="up"
            in={isOpen || !!inlineLocationQuestion}
            timeout={{ appear: 200, enter: 700 }}
            mountOnEnter
            unmountOnExit
          >
            <MapPopupContainer className={"location-popup-container"}>
              <CloseButton
                className={"location-close"}
                onClick={handleCloseClicked}
              />
              <TopTextArea className={"top-text-area"}>
                <QuestionText>
                  {translate(LOCATION_MAP_TITLE_TEXT)}
                </QuestionText>
                {!!selectedFeatureNames?.length && (
                  <Qualifiers>{selectedFeatureNames.join(", ")}</Qualifiers>
                )}
              </TopTextArea>
              {/* In case the solver asked for multiple maps show navigation and allow the user to choose */}
              {filteredMaps?.length > 1 && (
                <LocationMapSelector
                  maps={filteredMaps}
                  current={currentIndex}
                  onSelected={handleMapChanged}
                />
              )}
              {filteredMaps?.[currentIndex] && (
                <LocationMap
                  locationQuestion={filteredMaps?.[currentIndex]}
                  supportedFeatures={supportedFeatures}
                  selectedIds={selectedIds}
                  onSelect={handleMapItemSelected}
                />
              )}
              {!!matchingMaps?.length && (
                <Footer>
                  <ButtonsDock>
                    <Continue
                      disabled={!selectedIds?.length}
                      onClick={handleDone}
                      skipScroll
                    />
                  </ButtonsDock>
                </Footer>
              )}
            </MapPopupContainer>
          </Slide>
        </LocationRefinementQuestionView>
      </Fade>
    </>
  );
};
